export interface JobErrorExplanation {
  /** Short, plain-language summary of what went wrong. */
  summary: string;
  /** Concrete, ordered steps an engineer can take to resolve it. */
  steps: string[];
}

/**
 * Translates a raw worker/FTP error string into a plain-language explanation
 * with actionable resolution steps. The raw code (e.g. "530 Login incorrect.")
 * is rarely enough on its own, so every branch tells the user *what to do next*.
 * Falls back to generic-but-still-actionable guidance when the error isn't
 * recognised, so the user is never left with just a code.
 */
export function explainJobError(
  raw: string | null | undefined,
): JobErrorExplanation | null {
  if (!raw) return null;
  const e = raw.toLowerCase();

  // FTP 503 — the server demands an AUTH (TLS) handshake before login. Happens
  // when a job is configured as plain FTP against a server that requires FTPS.
  if (e.includes('use auth first') || e.includes('503')) {
    return {
      summary:
        'The FTP server requires an encrypted (TLS) connection, but this import is set up as plain FTP.',
      steps: [
        'Edit the import and switch the protocol to FTPS.',
        'Use implicit FTPS on port 990 (most Bullhorn hosts), or explicit FTPS on port 21.',
        'Re-run the import.',
      ],
    };
  }

  // FTP 530 — credentials rejected after the connection succeeded.
  if (
    e.includes('login incorrect') ||
    e.includes('530') ||
    e.includes('not logged in')
  ) {
    return {
      summary: 'The FTP server rejected the username or password.',
      steps: [
        'Double-check the FTP username — it is case-sensitive.',
        'Re-enter the FTP password; it may be wrong, expired, or not yet provisioned by the client.',
        'Confirm the account is active with the source provider, then re-run.',
      ],
    };
  }

  // DNS / host resolution.
  if (e.includes('enotfound') || e.includes('getaddrinfo')) {
    return {
      summary: "The FTP host name couldn't be found (DNS lookup failed).",
      steps: [
        'Check the host is spelled correctly (e.g. ftp-uk2.bullhorn.com).',
        'Remove any leading "ftp://" or trailing slash from the host field.',
        'Re-run once corrected.',
      ],
    };
  }

  // Connection refused — usually a wrong port for the chosen protocol.
  if (e.includes('econnrefused')) {
    return {
      summary: 'The FTP server refused the connection on that port.',
      steps: [
        'Make sure the port matches the protocol: 21 for FTP / explicit FTPS, 990 for implicit FTPS, 22 for SFTP.',
        'Confirm the server is reachable and accepting connections.',
        'Re-run once corrected.',
      ],
    };
  }

  // Timeouts — host/port wrong, server down, or firewall blocking us.
  if (
    e.includes('etimedout') ||
    e.includes('timed out') ||
    e.includes('timeout')
  ) {
    return {
      summary: 'The connection to the FTP server timed out.',
      steps: [
        'Confirm the host and port are correct and the server is online.',
        'Ask the client to whitelist our outbound IP if they sit behind a firewall.',
        'Re-run to retry.',
      ],
    };
  }

  // 550 / path / permission.
  if (
    e.includes('550') ||
    e.includes('no such file') ||
    e.includes('permission denied')
  ) {
    return {
      summary:
        'The path on the FTP server is missing or not accessible to this account.',
      steps: [
        'Check the Path field points to the correct file or folder.',
        'Confirm the account has read access to that path.',
        'Re-run once corrected.',
      ],
    };
  }

  // TLS / certificate problems.
  if (
    e.includes('certificate') ||
    e.includes('handshake') ||
    e.includes('tls') ||
    e.includes('ssl')
  ) {
    return {
      summary: 'The secure (TLS) handshake with the FTP server failed.',
      steps: [
        'Confirm whether the server expects implicit FTPS (port 990) or explicit FTPS (port 21).',
        'Verify the protocol and port combination on the import.',
        'Re-run once corrected.',
      ],
    };
  }

  // Generic fallback — still point the user somewhere useful.
  return {
    summary:
      'The import failed before it could finish. The raw error is shown below.',
    steps: [
      'Review the FTP connection details for this import.',
      'Correct anything that looks wrong and re-run.',
      'If it keeps failing, share the raw error with engineering.',
    ],
  };
}

/**
 * Builds a multi-line plain-text tooltip from an error explanation, suitable for
 * a native `title` attribute. Returns the raw error unchanged when it can't be
 * explained.
 */
export function jobErrorTooltip(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const info = explainJobError(raw);
  if (!info) return raw;
  return `${info.summary}\n\nHow to fix:\n${info.steps
    .map((s) => `• ${s}`)
    .join('\n')}`;
}
